/* eslint-disable consistent-return */
// MUST be first import: overrides webpack's auto publicPath when the embed
// script is loaded with async/defer (document.currentScript is null in that
// case, causing webpack's last-script-tag fallback to misidentify the bundle).
import './setPublicPath';
// Reads the address hash before any other embed code, or a host script, can change it.
import './router/bootHash';
// Installs runtime polyfills (structuredClone, Array.prototype.at) for browsers
// older than the embed's modern-API usage, where the missing feature otherwise
// throws mid-render and blanks the embed. Must run before any component code.
import './polyfills';
import { AppContextProvider } from 'AppProvider';
import ErrorBoundary from 'components/ErrorBoundary';
import { ScrollToTop } from 'components/ScrollToTop/ScrollToTop';
import { OneTrustProvider } from 'components/OneTrust/OneTrustProvider';
import { KOUTO_EMBED_ROOT } from 'consts';
import ContainerDimensionProvider from 'ContainerDimensionProvider';
import isEqual from 'lodash/isEqual';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { initializeRouter } from 'router';
import { StyleSheetManager } from 'styled-components';
import { SWRConfig } from 'swr';
import { ToastProvider } from 'ToastProvider';
// Side-effect import: runs i18n.init() and language detection before React mounts,
// so currentI18n.language is reliable on first render.
import 'translations/i18n';
import { setAxiosHeader } from 'utils/axios';
import { setFetchHeader } from 'utils/fetch';
import { RequestCache } from 'utils/requestCache';
import WindowDimensionsProvider from 'WindowDimensionProvider';
import { DateLocaleProvider } from 'context/DateLocaleContext';
import App from './App';
import NotFound from './NotFound';

let root = null;

// Create global Kouto namespace
if (!window.Kouto) {
  window.Kouto = {};
}

function initApp() {
  const koutoEmbedContainer = document.getElementById(KOUTO_EMBED_ROOT);
  if (!koutoEmbedContainer) {
    console.error(
      new Error(
        "Can't find kouto-embed-root element - please check your integration",
      ),
    );
    return false;
  }

  if (koutoEmbedContainer.getAttribute('data-kouto-initialized')) {
    return true;
  }

  koutoEmbedContainer.setAttribute('data-kouto-initialized', true);

  koutoEmbedContainer.innerHTML = `
    <div id="kouto-app-root"></div><div id="kouto-modal-root"></div>
  `;
  const koutoAppContainer = document.getElementById('kouto-app-root');

  let brandId = null;
  const isDev = isEqual(process.env.NODE_ENV, 'development');

  if (isEqual(process.env.NODE_ENV, 'production')) {
    brandId = koutoEmbedContainer.dataset.brandId;
    console.info('production');
    console.info('brandId', brandId);
  }

  // for staging environment
  if (!brandId) {
    const brandIdRegexp =
      /\/brands\/\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/;
    if (brandIdRegexp.test(window.location.pathname)) {
      const idMatches = window.location.pathname.match(brandIdRegexp);
      brandId = idMatches[0].split('/')[2];
    }
  }

  const { Router, routerProps } = initializeRouter(brandId);

  // for development
  if (isDev && !brandId) {
    brandId = process.env.KOUTO_BRAND_ID;
  }
  setFetchHeader('Kouto-Brand-Id', brandId);
  setFetchHeader('Way-Brand-Id', brandId);
  setAxiosHeader('Kouto-Brand-Id', brandId);
  setAxiosHeader('Way-Brand-Id', brandId);

  const ContextWrapper = () => {
    if (!brandId) {
      return <NotFound isDev={isDev} />;
    }

    return (
      <StyleSheetManager namespace="#kouto-embed-root">
        <AppContextProvider brandId={brandId}>
          <OneTrustProvider brandId={brandId}>
            <ToastProvider>
              <WindowDimensionsProvider>
                <ContainerDimensionProvider>
                  <ErrorBoundary>
                    <Router {...routerProps}>
                      <ScrollToTop />
                      <SWRConfig value={{ revalidateOnFocus: false }}>
                        <DateLocaleProvider>
                          <App />
                        </DateLocaleProvider>
                      </SWRConfig>
                    </Router>
                  </ErrorBoundary>
                </ContainerDimensionProvider>
              </WindowDimensionsProvider>
            </ToastProvider>
          </OneTrustProvider>
        </AppContextProvider>
      </StyleSheetManager>
    );
  };
  root = ReactDOM.createRoot(koutoAppContainer);
  root.render(<ContextWrapper />);

  return true;
}

const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.type !== 'childList') return;

    mutation.addedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.id !== KOUTO_EMBED_ROOT) return;
      if (node.getAttribute('data-kouto-initialized')) {
        console.info('Kouto embed root already initialized, skipping...');
        return;
      }
      console.info('Kouto embed root added to DOM, initializing...');
      initApp();
    });

    mutation.removedNodes.forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (node.id !== KOUTO_EMBED_ROOT) return;
      if (!root) return;

      // Only unmount if the node is actually removed from the document
      // and not just being moved in the DOM
      setTimeout(() => {
        // Check if element was re-added within 100ms (DOM move operation)
        const stillExists = document.getElementById(KOUTO_EMBED_ROOT);
        if (!stillExists || stillExists !== node) {
          console.warn('Kouto embed root removed from DOM, unmounting...');
          node.removeAttribute('data-kouto-initialized');
          root.unmount();
          RequestCache.clearAllCache();
        } else {
          console.info(
            'Kouto embed root was moved but still exists, not unmounting',
          );
        }
      }, 100);
    });
  });
});

// Expose initApp as window.Kouto.init for manual initialization
window.Kouto.init = initApp;

initApp();

observer.observe(document, {
  childList: true,
  subtree: true,
});
